import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useEffectiveRole } from "@/contexts/PreviewContext";
import { formatCurrency, getAnnualReference, getPricingBand, PRICING_BANDS } from "@/lib/pricingCalculator";

const SLIDER_MIN = 0;
const SLIDER_MAX = 325_000;
const SLIDER_STEP = 1_000;

const TICKS = [
  { value: 0, label: "$0" },
  { value: 10_000, label: "$10K" },
  { value: 25_000, label: "$25K" },
  { value: 50_000, label: "$50K" },
  { value: 75_000, label: "$75K" },
  { value: 100_000, label: "$100K" },
  { value: 200_000, label: "$200K" },
  { value: 300_000, label: "$300K+" },
];

function CalculatorGlyph({ className = "" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <rect x="4" y="2.75" width="16" height="18.5" rx="2.25" />
      <path d="M7.5 6.75h9M7.75 11h.01M12 11h.01M16.25 11h.01M7.75 15h.01M12 15h.01M16.25 15h.01M7.75 18.25h.01M12 18.25h4.25" strokeLinecap="round" />
    </svg>
  );
}

function formatExpenseValue(value: number) {
  if (value >= 300_000) return "$300K+";
  if (value === 0) return "$0";
  return value >= 1_000 ? `$${Math.round(value / 1_000)}K` : formatCurrency(value);
}

export default function PricingCalculator() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole(user?.role ?? "bookkeeper");
  const [, navigate] = useLocation();
  const [monthlyExpenses, setMonthlyExpenses] = useState(62_500);
  const selectedBand = useMemo(() => getPricingBand(monthlyExpenses), [monthlyExpenses]);
  const price = selectedBand.monthlyPrice;
  const isCustomPricing = price === null;
  const sliderProgress = Math.min(100, Math.max(0, ((monthlyExpenses - SLIDER_MIN) / (SLIDER_MAX - SLIDER_MIN)) * 100));
  const hasAccess = ["super_admin", "admin", "controller", "asst_controller"].includes(effectiveRole);

  if (!hasAccess) {
    return (
      <DashboardLayout>
        <section className="mx-auto max-w-xl rounded-2xl border border-[oklch(0.83_0.025_240)] bg-white p-8 text-center shadow-sm">
          <CalculatorGlyph className="mx-auto mb-3 h-9 w-9 text-[oklch(0.55_0.13_75)]" />
          <h1 className="text-2xl font-bold text-[oklch(0.12_0.04_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>Pricing Calculator</h1>
          <p className="mt-2 text-sm text-[oklch(0.38_0.035_240)]">This internal reference is available to Controllers.</p>
          <button onClick={() => navigate("/dashboard")} className="mt-5 rounded-lg border border-[oklch(0.32_0.045_240)] px-4 py-2 text-sm font-semibold text-[oklch(0.15_0.04_240)]">Return to Dashboard</button>
        </section>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <main className="mx-auto w-full max-w-6xl space-y-5 pb-8 animate-fade-slide-up">
        <header className="rounded-2xl border border-[oklch(0.8_0.035_240)] bg-[linear-gradient(118deg,oklch(0.14_0.06_240),oklch(0.21_0.075_230))] px-5 py-5 text-white shadow-[0_16px_32px_oklch(0.17_0.05_240/0.18)] sm:px-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-[oklch(0.72_0.14_75/0.18)] text-[oklch(0.84_0.14_82)]">
                <CalculatorGlyph className="h-6 w-6" />
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[oklch(0.82_0.13_80)]">Controller Reference</p>
                <h1 className="mt-0.5 text-2xl font-bold sm:text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>Pricing Calculator</h1>
                <p className="mt-1 text-sm text-[oklch(0.89_0.015_240)]">Select expected monthly expenses to view the current pricing-model reference.</p>
              </div>
            </div>
            <span className="rounded-full border border-[oklch(0.78_0.13_78/0.45)] bg-[oklch(0.72_0.14_75/0.16)] px-3 py-1.5 text-xs font-semibold text-[oklch(0.92_0.11_85)]">Evaluation Model</span>
          </div>
        </header>

        <section className="grid overflow-hidden rounded-2xl border border-[oklch(0.82_0.025_240)] bg-white shadow-[0_12px_28px_oklch(0.18_0.04_240/0.08)] lg:grid-cols-[0.92fr_1.08fr]">
          <div className="border-b border-[oklch(0.88_0.018_240)] bg-[linear-gradient(150deg,oklch(0.99_0.006_240),oklch(0.965_0.014_75))] p-6 lg:border-b-0 lg:border-r lg:p-8">
            <div className="flex h-full flex-col">
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[oklch(0.16_0.06_240)] text-[oklch(0.79_0.14_80)] shadow-sm"><CalculatorGlyph className="h-6 w-6" /></span>
              <h2 className="text-2xl font-bold text-[oklch(0.12_0.04_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>Accounting</h2>
              <p className="mt-1 max-w-md text-sm font-medium text-[oklch(0.36_0.055_240)]">Integrated Accounting. Tax Ready Financials.</p>
              <div className="mt-6 space-y-2.5">
                {[
                  "AI-powered, human-managed bookkeeping",
                  "Direct bookkeeper live chat",
                  "Industry-specific transaction categorization",
                  "Continuous automatic book reconciliation",
                  "Seamless integration with financial sources",
                  "Crucial financial insights",
                  "Dedicated accounting dashboard",
                  "Easy financial data exports",
                  "Comprehensive tax filings add-on",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm font-medium text-[oklch(0.18_0.045_240)]">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[oklch(0.73_0.14_78)] text-[10px] font-bold text-[oklch(0.13_0.04_240)]">✓</span>
                    {item}
                  </div>
                ))}
              </div>
              <div className="mt-auto pt-8 text-xs text-[oklch(0.42_0.035_240)]">Internal APX WorkCenter reference · Current evaluated model</div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.13em] text-[oklch(0.42_0.04_240)]">Expected monthly expenses</p>
                <p className="mt-2 text-3xl font-bold tabular-nums text-[oklch(0.12_0.04_240)]">{formatExpenseValue(monthlyExpenses)}</p>
              </div>
              <span className="rounded-lg border border-[oklch(0.78_0.1_75)] bg-[oklch(0.96_0.05_80)] px-3 py-1.5 text-sm font-bold text-[oklch(0.27_0.075_75)]">{selectedBand.label}</span>
            </div>

            <div className="mt-8">
              <div className="relative pb-9 pt-6">
                <div className="pointer-events-none absolute inset-x-1 top-[1.42rem] flex h-6 items-start justify-between" aria-hidden="true">
                  {Array.from({ length: 46 }, (_, index) => {
                    const isMajor = index % 5 === 0;
                    const isActive = (index / 45) * 100 <= sliderProgress;
                    return <span key={index} className="w-px" style={{ height: isMajor ? 22 : index % 2 === 0 ? 15 : 9, background: isActive ? "oklch(0.63 0.14 75)" : "oklch(0.65 0.055 240)" }} />;
                  })}
                </div>
                <span
                  className="pointer-events-none absolute top-0 z-20 -translate-x-1/2 rounded-md border border-[oklch(0.67_0.11_75)] bg-[oklch(0.16_0.055_240)] px-2.5 py-1 text-xs font-bold text-white shadow-md"
                  style={{ left: `${sliderProgress}%` }}
                >
                  {selectedBand.label}
                  <i className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 border-b border-r border-[oklch(0.67_0.11_75)] bg-[oklch(0.16_0.055_240)]" />
                </span>
                <input
                  aria-label="Expected monthly expenses"
                  type="range"
                  min={SLIDER_MIN}
                  max={SLIDER_MAX}
                  step={SLIDER_STEP}
                  value={monthlyExpenses}
                  onChange={(event) => setMonthlyExpenses(Number(event.target.value))}
                  className="relative z-10 h-8 w-full cursor-ew-resize appearance-none bg-transparent outline-none [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-[oklch(0.72_0.14_75)] [&::-moz-range-thumb]:shadow-md [&::-webkit-slider-runnable-track]:h-6 [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:mt-0.5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[oklch(0.72_0.14_75)] [&::-webkit-slider-thumb]:shadow-md"
                />
                <div className="pointer-events-none absolute inset-x-1 top-[3.2rem] flex justify-between">
                  {TICKS.map((tick) => (
                    <span key={tick.value} className="relative flex flex-col items-center text-[10px] font-semibold text-[oklch(0.43_0.035_240)] sm:text-xs">
                      <span className={tick.value === 300_000 ? "translate-x-1.5" : ""}>{tick.label}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-7 rounded-2xl border border-[oklch(0.8_0.04_75)] bg-[linear-gradient(135deg,oklch(0.99_0.02_75),oklch(0.975_0.01_240))] p-5 sm:p-6">
              <p className="text-xs font-bold uppercase tracking-[0.13em] text-[oklch(0.39_0.045_240)]">Reference amount</p>
              {isCustomPricing ? (
                <div className="mt-3">
                  <p className="text-3xl font-bold text-[oklch(0.14_0.045_240)] sm:text-4xl" style={{ fontFamily: "'Playfair Display', serif" }}>Custom Pricing</p>
                  <p className="mt-2 text-sm text-[oklch(0.36_0.035_240)]">Expense level exceeds the evaluated model range. Set a tailored reference amount.</p>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <p className="text-5xl font-bold tracking-tight text-[oklch(0.12_0.045_240)] sm:text-6xl" style={{ fontFamily: "'Playfair Display', serif" }}>{formatCurrency(price)}</p>
                    <p className="mt-1 text-sm font-medium text-[oklch(0.34_0.04_240)]">monthly reference amount</p>
                  </div>
                  <div className="rounded-xl border border-[oklch(0.82_0.035_240)] bg-white px-4 py-3 text-right shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[oklch(0.45_0.04_240)]">Annual reference</p>
                    <p className="mt-1 text-xl font-bold tabular-nums text-[oklch(0.18_0.055_240)]">{formatCurrency(getAnnualReference(price))}</p>
                    <p className="mt-0.5 text-[11px] font-semibold text-[oklch(0.4_0.12_150)]">17% model reduction</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-[oklch(0.84_0.022_240)] bg-white p-5 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 className="text-lg font-bold text-[oklch(0.14_0.045_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>Evaluated price bands</h2>
              <p className="mt-1 text-sm text-[oklch(0.39_0.035_240)]">The slider selects from the same reference bands shown below.</p>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            {PRICING_BANDS.map((band) => {
              const active = band.label === selectedBand.label;
              return (
                <button
                  key={band.label}
                  type="button"
                  onClick={() => setMonthlyExpenses(band.max === null ? 305_000 : Math.round((band.min + band.max) / 2))}
                  className="rounded-xl border p-3 text-left transition-all duration-150 hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(0.68_0.14_75)]"
                  style={{
                    borderColor: active ? "oklch(0.66 0.14 75)" : "oklch(0.86 0.02 240)",
                    background: active ? "oklch(0.97 0.04 78)" : "white",
                  }}
                >
                  <p className="text-xs font-semibold text-[oklch(0.4_0.035_240)]">{band.label} / month</p>
                  <p className="mt-1 text-lg font-bold text-[oklch(0.14_0.045_240)]">{band.monthlyPrice === null ? "Custom Pricing" : formatCurrency(band.monthlyPrice)}</p>
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </DashboardLayout>
  );
}
